import { useState } from 'react';
import type { ChallengeType, Difficulty } from '../../../utils/mathProblems';

const CHALLENGE_TYPES: { value: ChallengeType; label: string; desc: string }[] = [
  { value: 'multiplication', label: 'Multiplication', desc: '×' },
  { value: 'division', label: 'Division', desc: '÷' },
  { value: 'volume', label: 'Volume', desc: '3D' },
];

const DIFFICULTIES: { value: Difficulty; label: string; color: string }[] = [
  { value: 'easy', label: 'Easy', color: 'text-green-600' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'hard', label: 'Hard', color: 'text-orange-600' },
  { value: 'expert', label: 'Expert', color: 'text-red-600' },
];

interface Props {
  onCreateRoom: (name: string, gameType: ChallengeType, difficulty: Difficulty) => void;
  onJoinRoom: (code: string, name: string) => void;
  isLoading: boolean;
  error: string | null;
}

export default function LobbyScreen({ onCreateRoom, onJoinRoom, isLoading, error }: Props) {
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [name, setName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [gameType, setGameType] = useState<ChallengeType>('multiplication');
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');

  const handleCreate = () => {
    if (!name.trim()) return;
    onCreateRoom(name.trim(), gameType, difficulty);
  };

  const handleJoin = () => {
    if (!name.trim() || joinCode.length < 4) return;
    onJoinRoom(joinCode.trim(), name.trim());
  };

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-sky-100 rounded-full mx-auto mb-3">
          <i className="ri-global-line text-3xl text-sky-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
          Online Match
        </h2>
        <p className="text-gray-500 text-sm mt-1">Real-time online play · First-person 3D tennis</p>
      </div>

      {/* How it works */}
      <div className="flex items-center gap-4 bg-sky-50 rounded-xl px-6 py-3 text-xs text-sky-700 max-w-md w-full">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">1</span>
          <span>Host creates a room → gets a 4-letter code</span>
        </div>
        <i className="ri-arrow-right-s-line text-sky-400 flex-shrink-0" />
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">2</span>
          <span>Friend enters the code → joins!</span>
        </div>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-md">
        {/* Tab switcher */}
        <div className="flex bg-gray-100 rounded-full p-1 mb-5">
          <button
            onClick={() => setTab('create')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
              tab === 'create' ? 'bg-sky-600 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-add-circle-line" />Create Room
          </button>
          <button
            onClick={() => setTab('join')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
              tab === 'join' ? 'bg-sky-600 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className="ri-door-open-line" />Join Room
          </button>
        </div>

        {/* Name field (shared) */}
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Your Nickname</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter your name..."
            maxLength={20}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
          />
        </div>

        {tab === 'create' ? (
          <>
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Challenge Type</label>
              <div className="flex gap-2">
                {CHALLENGE_TYPES.map(ct => (
                  <button
                    key={ct.value}
                    onClick={() => setGameType(ct.value)}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                      gameType === ct.value
                        ? 'border-sky-400 bg-sky-50 text-sky-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-base font-bold">{ct.desc}</span>
                    {ct.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Difficulty</label>
              <div className="flex gap-2">
                {DIFFICULTIES.map(d => (
                  <button
                    key={d.value}
                    onClick={() => setDifficulty(d.value)}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                      difficulty === d.value
                        ? 'border-sky-400 bg-sky-50 text-sky-700'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <span className={difficulty === d.value ? 'text-sky-700' : d.color}>{d.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleCreate}
              disabled={!name.trim() || isLoading}
              className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Creating room...
                </span>
              ) : 'Create Room'}
            </button>
          </>
        ) : (
          <>
            <div className="mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                Room Code <span className="text-sky-500 normal-case font-normal">(the 4-letter code from the host)</span>
              </label>
              <input
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="e.g. A4BX"
                maxLength={4}
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100 font-mono text-center text-2xl tracking-[0.35em] uppercase"
              />
            </div>

            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-700">
              <i className="ri-information-line mr-1" />
              Enter the code from the person who created the room. They must be on the waiting screen for you to join.
            </div>

            <button
              onClick={handleJoin}
              disabled={!name.trim() || joinCode.length < 4 || isLoading}
              className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Joining...
                </span>
              ) : 'Join Room'}
            </button>
          </>
        )}

        {error && (
          <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
